/**
 * Shared HTTP client for the Express API.
 *
 * Every endpoint module used to carry its own copy of the same three pieces —
 * an `authFetchOpts` object, a `parseResponse` helper, and the per-call
 * fetch/parse/catch ladder — which had drifted into six different conventions
 * across eight files. This is the single implementation they now share.
 *
 * Error convention: failures come back as an `{ error }` sentinel rather than a
 * thrown exception, which is what the majority of call sites already expect and
 * what `isErrorResponse` (utils/apiUtils.ts) narrows. Callers therefore never
 * need to know whether a given endpoint throws.
 *
 * All requests use `credentials: 'include'` so the browser sends the httpOnly
 * `zt_token` session cookie automatically. No token parameter is required.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export interface ErrorResponse {
    error: string;
}

/** Common fetch options used by every authenticated endpoint. */
const authFetchOpts: RequestInit = {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
};

export interface RequestOptions {
    /**
     * How to handle a fetch that never reaches the server (offline, DNS, CORS).
     *
     * Omitted, the rejection propagates to the caller — the historical behavior
     * of the project and activity endpoints. Supply a string, or a function of
     * the thrown value, to convert it into an `{ error }` sentinel instead.
     */
    networkErrorMessage?: string | ((e: unknown) => string);
}

/**
 * Reads a response body as JSON, converting a non-JSON body (an HTML error page
 * from a proxy, say) into the `{ error }` sentinel rather than throwing.
 *
 * Note this deliberately does not branch on `resp.ok`: the API returns its own
 * `{ error }` body for 4xx/5xx, and that body is more useful than a synthesized
 * status message. The status is only used when the body cannot be parsed.
 */
async function parseResponse<T>(resp: Response): Promise<T | ErrorResponse> {
    const text = await resp.text();
    try {
        return JSON.parse(text) as T;
    } catch {
        return { error: `Server error (${resp.status})` };
    }
}

/**
 * Omits the request body entirely when none was supplied, rather than sending
 * an empty `{}` — some endpoints (e.g. the heartbeat wake ping) are called with
 * no body today, and sending one would change what goes over the wire.
 */
function serializeBody(body: unknown): string | undefined {
    return body === undefined ? undefined : JSON.stringify(body);
}

async function request<T>(
    path: string,
    init: RequestInit,
    options: RequestOptions = {},
): Promise<T | ErrorResponse> {
    try {
        const resp = await fetch(`${API_BASE}${path}`, { ...authFetchOpts, ...init });
        return await parseResponse<T>(resp);
    } catch (e) {
        const { networkErrorMessage } = options;
        if (networkErrorMessage === undefined) throw e;
        return {
            error: typeof networkErrorMessage === 'function' ? networkErrorMessage(e) : networkErrorMessage,
        };
    }
}

export function getJson<T = unknown>(path: string, options?: RequestOptions): Promise<T | ErrorResponse> {
    return request<T>(path, {}, options);
}

export function postJson<T = unknown>(path: string, body?: unknown, options?: RequestOptions): Promise<T | ErrorResponse> {
    return request<T>(path, { method: 'POST', body: serializeBody(body) }, options);
}

export function putJson<T = unknown>(path: string, body?: unknown, options?: RequestOptions): Promise<T | ErrorResponse> {
    return request<T>(path, { method: 'PUT', body: serializeBody(body) }, options);
}

export function patchJson<T = unknown>(path: string, body?: unknown, options?: RequestOptions): Promise<T | ErrorResponse> {
    return request<T>(path, { method: 'PATCH', body: serializeBody(body) }, options);
}

export function deleteJson<T = unknown>(path: string, options?: RequestOptions): Promise<T | ErrorResponse> {
    return request<T>(path, { method: 'DELETE' }, options);
}
